let cachedKnowledgeBase = null;
let isFetching = false;
let fetchPromise = null;

const STOPWORDS = new Set([
  'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'de', 'del', 'a', 'al', 'en', 'y', 'o',
  'que', 'es', 'son', 'se', 'su', 'sus', 'lo', 'le', 'con', 'por', 'para', 'como', 'cual',
  'cuando', 'donde', 'me', 'te', 'nos', 'usted', 'mi', 'tu', 'si', 'no', 'pero', 'mas', 'muy',
  'the', 'a', 'an', 'is', 'are', 'to', 'of', 'and', 'or', 'for', 'how', 'what', 'do', 'i', 'you',
  'precio', 'precios', 'costo', 'cuanto', 'cuesta', 'pago', 'pagar', 'licencia', 'suscripcion',
]);

const stripAccents = (s) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const tokenize = (text) =>
  stripAccents(text)
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 2 && !STOPWORDS.has(t));

const normalize = (value) => stripAccents(value || '');

async function fetchKnowledgeBase() {
  if (cachedKnowledgeBase) return cachedKnowledgeBase;
  if (isFetching && fetchPromise) return fetchPromise;

  isFetching = true;
  fetchPromise = fetch('/data/knowledge_base.json')
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      cachedKnowledgeBase = data;
      isFetching = false;
      fetchPromise = null;
      return data;
    })
    .catch((err) => {
      console.error('[chatQAService] Error fetching knowledge base:', err);
      isFetching = false;
      fetchPromise = null;
      return null;
    });

  return fetchPromise;
}

function buildChunkIndex(kb) {
  if (!kb || !kb.chunks) return [];
  return kb.chunks.map((chunk) => {
    const keywords = chunk.keywords || [];
    const keywordTokens = new Set(keywords.map(normalize));
    const contentTokens = new Set(tokenize(chunk.content));
    const titleTokens = new Set(tokenize(chunk.title));
    return { ...chunk, keywordTokens, contentTokens, titleTokens };
  });
}

let cachedIndex = null;

function getIndex(kb) {
  if (!cachedIndex && kb && kb.chunks) {
    cachedIndex = buildChunkIndex(kb);
  }
  return cachedIndex || [];
}

function scoreChunk(chunk, queryTokens) {
  let score = 0;
  for (const qt of queryTokens) {
    if (chunk.titleTokens.has(qt)) score += 5;
    if (chunk.keywordTokens.has(qt)) score += 4;
    if (chunk.contentTokens.has(qt)) score += 2;
    if (chunk.title.includes(qt)) score += 1;
  }
  if (queryTokens.length > 0 && score > 0) {
    score = score / queryTokens.length;
  }
  return score;
}

const DEFAULT_THRESHOLD = 0.3;
const FALLBACK_THRESHOLD = 0.1;

export async function findAnswer(query, options = {}) {
  const threshold = options.threshold ?? DEFAULT_THRESHOLD;
  const fallbackThreshold = options.fallbackThreshold ?? FALLBACK_THRESHOLD;
  const maxSources = options.maxSources ?? 3;

  if (!query || typeof query !== 'string' || query.trim().length < 2) {
    return {
      reply: '¿Podrías preguntar con más detalle? Estoy aquí para ayudarte con información sobre Vortex EXANI-II.',
      sources: [],
      isFallback: true,
    };
  }

  const kb = await fetchKnowledgeBase();
  if (!kb || !kb.chunks || kb.chunks.length === 0) {
    return {
      reply: 'Lo siento, no tengo conexión con la base de conocimiento en este momento. Escríbenos a vortexadmision@gmail.com para más detalles.',
      sources: [],
      isFallback: true,
    };
  }

  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) {
    return {
      reply: kb.chunks[0]?.content || 'No tengo información sobre eso.',
      sources: [],
      isFallback: true,
    };
  }

  const index = getIndex(kb);
  const scored = index
    .map((chunk) => ({ chunk, score: scoreChunk(chunk, queryTokens) }))
    .sort((a, b) => b.score - a.score);

  const bestScore = scored[0]?.score || 0;
  const thresholdToUse = bestScore < threshold ? fallbackThreshold : threshold;
  const matches = scored
    .filter((s) => s.score >= thresholdToUse)
    .slice(0, maxSources);

  if (matches.length === 0) {
    const randomChunk = index[Math.floor(Math.random() * index.length)];
    return {
      reply: `Lo siento, no tengo información específica sobre "${query}". Puedes escribirnos a vortexadmision@gmail.com para más detalles.`,
      sources: [randomChunk?.title],
      isFallback: true,
    };
  }

  const content = matches.map((m) => m.chunk.content).join('\n\n');
  const sources = matches.map((m) => m.chunk.title);

  return {
    reply: content,
    sources,
    isFallback: bestScore < threshold,
  };
}

export async function getSuggestedQuestions() {
  const kb = await fetchKnowledgeBase();
  if (!kb || !kb.chunks) return [];
  return kb.chunks
    .slice(0, 8)
    .map((c) => c.title);
}

export default { findAnswer, getSuggestedQuestions };
